/**
 * Helpers for keeping a simple list's search and column sort in the URL, so a reload, back/forward
 * navigation or a shared link restores them. Used by the lists that have no other filters
 * (Campaigns, QR Codes, PURLs, Tracking Phone Numbers); lists with filters keep their own
 * `<list>QueryParams` util (Orders, Contacts, Templates).
 *
 * Query param names:
 *   search             – free-text search string
 *   sortBy / sortOrder – column sort; `sortBy` must be one of the list's sort keys
 *
 * The history tables (QR Codes, PURLs, Tracking Phone call / SMS history) keep only the column sort
 * in the URL, using the history sort keys below.
 */

import { buildSortQueryParams, parseSortSearchParams } from '~/utils/sort-params';

// Sort keys per list. Each must match the API allowlist of the list's endpoint.
export const CAMPAIGN_SORT_KEYS = ['name', 'mailerActions', 'updatedAt', 'createdBy', 'createdAt'];
// QR Codes and PURLs share one endpoint (GET /qr-code-instances).
export const ENGAGEMENT_SORT_KEYS = ['campaignName', 'type', 'totalScans', 'status'];
// QR Code and PURL history share one endpoint (GET /qr-code-instances/:id/scanned-contacts).
export const ENGAGEMENT_HISTORY_SORT_KEYS = [
  'fullName',
  'companyName',
  'mailingAddress',
  'campaignName',
  'totalScans',
  'lastScan',
];
// Call history (GET /tracking-phone-numbers/history/:id) and SMS history (.../sms-history/:id).
export const PHONE_CALL_HISTORY_SORT_KEYS = [
  'createdAt',
  'callFrom',
  'callTo',
  'callDuration',
  'status',
];
export const PHONE_SMS_HISTORY_SORT_KEYS = ['createdAt', 'smsFrom', 'smsTo', 'messageText'];
export const PHONE_TRACKING_SORT_KEYS = [
  'name',
  'phoneNumber',
  'forwardingPhoneNumber',
  'numberOfCalls',
  'numberOfSms',
  'status',
  'lastReceivedAt',
  'createdAt',
];

/**
 * @param {URLSearchParams} searchParams
 * @param {string[]} sortKeys allowed `sortBy` values; anything else is ignored
 * @returns {{ search: string, sort: { sortBy?: string, sortOrder?: string } }}
 */
export const parseSearchSortQueryParams = (searchParams, sortKeys) => ({
  search: (searchParams.get('search') || '').trim(),
  sort: parseSortSearchParams(searchParams, sortKeys),
});

/**
 * Plain object for URLSearchParams / setSearchParams. Empty values are omitted to keep the URL clean.
 *
 * @param {{ search?: string, sort?: { sortBy?: string, sortOrder?: string } }} state
 * @returns {Record<string, string>}
 */
export const buildSearchSortQueryParams = ({ search, sort } = {}) => {
  const params = {};

  if (search && search.trim()) {
    params.search = search.trim();
  }

  return { ...params, ...buildSortQueryParams(sort) };
};
